import { IconCoins, IconBolt, IconUsers, IconCalendar, IconTrendingUp, IconFileText, IconPencil, IconMicrophone } from '@tabler/icons-react';
import { futureFeatures } from './futureFeatures';

// Plan ladder (contract 1.8.0 `rank`): free < standard < pro. Only upgrades exist.
export const nextPlan = plan => ({ free: 'standard', standard: 'pro' })[plan] || null;

// What each paid plan offers, in the order shown (`planBenefit_<plan>_<key>` copy).
// `feature` ties a benefit to an unreleased Studio feature (enums/futureFeatures):
// it carries the "soon" label for as long as that feature is listed there. Personality is no
// plan benefit: it works on any plan while the balance covers the mapping (contract 2.3.0).
export const PLAN_BENEFITS = {
  standard: [
    { key: 'credits', icon: IconCoins },
    { key: 'automations', icon: IconBolt, feature: 'automations' },
    { key: 'leads', icon: IconUsers, feature: 'leads' },
  ],
  pro: [
    { key: 'credits', icon: IconCoins },
    { key: 'automations', icon: IconBolt, feature: 'automations' },
    { key: 'leads', icon: IconUsers, feature: 'leads' },
    { key: 'calendar', icon: IconCalendar, feature: 'calendar' },
    // Pro only: the catalog's `features.trending_topics`.
    { key: 'topics', icon: IconTrendingUp },
  ],
};

// The rows the plan cards compare, in order: every benefit any paid plan lists. A row a
// plan lacks borrows the copy of the plan that has it (`from`) and shows as not included.
const ROWS = [...new Set(Object.values(PLAN_BENEFITS).flat().map(benefit => benefit.key))];
export const comparisonRows = plan => ROWS.map(key => {
  const has = ([, list]) => list.some(item => item.key === key);
  const [from, list] = Object.entries(PLAN_BENEFITS).find(entry => entry[0] === plan && has(entry)) || Object.entries(PLAN_BENEFITS).find(has);
  return { key, from, included: from === plan, feature: list.find(item => item.key === key).feature };
});
// The rows a PlanCard shows, labelled: `credits` is the plan's monthly allowance, formatted.
// A row tied to an unreleased feature carries "soon".
const soon = feature => !!feature && futureFeatures.some(([key]) => key === feature);
export const planRows = (plan, t, credits) => comparisonRows(plan).map(row => ({
  key: row.key,
  included: row.included,
  soon: row.included && soon(row.feature),
  label: row.key === 'credits' ? t('planBenefit_credits', { credits }) : t(`planBenefit_${row.from}_${row.key}`),
}));

// What the plan scene lights up: the features a plan unlocks, or what packs pay for
// (`planArt_<plan>_<id>` copy).
export const PLAN_ART_OUTPUTS = {
  standard: [['automations', IconBolt], ['leads', IconUsers], ['scripts', IconFileText]],
  pro: [['automations', IconBolt], ['topics', IconTrendingUp], ['calendar', IconCalendar]],
  credits: [['scripts', IconFileText], ['revisions', IconPencil], ['audio', IconMicrophone]],
};

// The paid plans as a visitor sees them on the landing page, before sign-in: the
// catalog (`GET /plans`) needs a session. Allowances follow core `studio.payments.plans`;
// prices (cents) mirror the Stripe prices behind `studio_<plan>_monthly` and must be
// kept in step with them by hand until the core serves a public catalog.
export const PUBLIC_PLANS = {
  currency: 'brl',
  plans: [
    { plan: 'standard', monthly_credits: 5000, unit_amount: 7990 },
    { plan: 'pro', monthly_credits: 15000, unit_amount: 18990 },
  ],
};
